import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Blueprint, MagnifyingGlass, Plus, WarningCircle, Flask, CheckCircle, ArrowsClockwise,
  ShoppingCart, ChartLine, GraduationCap, CurrencyJpy, Clock, Hammer,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { DashboardZone, ActionTile, ListCard, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { devApi, STAGE_TONE } from './devApi';
import { useT } from '@/i18n/LanguageContext';

function DevRow({ d, onOpen, reason }) {
  const { t } = useT();
  return (
    <button type="button" onClick={() => onOpen(d.id)} data-testid={`dev-row-${d.id}`}
      className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-teal-50/50">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-zinc-900">{d.title}</p>
        <p className="truncate text-xs text-zinc-600">
          {d.development_type_label} · {d.project_name || 'プロジェクト未設定'}
          {d.target_completion_date ? ` · 目標 ${d.target_completion_date}` : ''}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
          <span className={d.owner_missing
            ? 'rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-semibold text-amber-900'
            : 'rounded-full border border-zinc-300 bg-white px-2 py-0.5 text-zinc-700'}
            data-testid={`dev-row-owner-${d.id}`}>
            担当: {d.owner_label || (d.owner_names?.length ? d.owner_names.join('、') : '担当者が未設定')}
          </span>
          {d.can_start_prototype === false && !d.prototypes && (
            <span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 font-semibold text-rose-900"
              data-testid={`dev-row-cannot-build-${d.id}`}>試作はまだ始められません</span>
          )}
          {d.can_build && (
            <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-800"
              data-testid={`dev-row-ready-${d.id}`}>部品はすべて揃っています</span>
          )}
          {d.is_overdue && (
            <span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 font-semibold text-rose-900"
              data-testid={`dev-row-overdue-${d.id}`}>
              {t('dev.badge.overdueDays', { n: d.overdue_days })}
            </span>
          )}
        </p>
        {(d.blockers || []).length > 0 && (
          <p className="mt-0.5 truncate text-xs text-amber-900" data-testid={`dev-row-blockers-${d.id}`}>
            止まっている理由: {d.blockers.slice(0, 2).join(' / ')}
            {d.blockers.length > 2 ? ` ほか${d.blockers.length - 2}件` : ''}
          </p>
        )}
        {!!d.prototypes && (
          <p className="mt-0.5 truncate text-xs text-zinc-700" data-testid={`dev-row-proto-${d.id}`}>
            試作 {d.prototypes}件{d.prototype_name ? ` · ${d.prototype_name}` : ''}
            {d.prototype_status_label ? `（${d.prototype_status_label}）` : ''}
          </p>
        )}
        {reason && <p className="mt-0.5 text-xs font-semibold text-amber-800">{reason}</p>}
      </div>
      {d.next_action && (
        <span className="hidden shrink-0 rounded-md border border-teal-300 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-900 sm:block"
          data-testid={`dev-row-next-action-${d.id}`}>次: {d.next_action}</span>
      )}
      <StatusBadge tone={STAGE_TONE[d.current_stage] || 'neutral'}>{d.current_stage_label}</StatusBadge>
    </button>
  );
}

export default function DevDashboard() {
  const nav = useNavigate();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [fab, setFab] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => { devApi.dashboard().then(setData).catch(() => setData({ counts: {}, attention: [], recent: [], by_stage: [] })); }, []);
  // 2026-06 · 製作依頼（自社製作）も「対応が必要」に出す。
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/engineering/fabrication/stats`)
      .then((r) => setFab(r.data)).catch(() => setFab(null));
  }, []);

  const open = (id) => nav(`/engineering/developments/${id}`);
  const search = (e) => {
    e.preventDefault();
    nav(`/engineering/developments${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);
  };
  const c = data?.counts || {};
  const attention = data?.attention || [];

  if (!data) {
    return (
      <div className="space-y-4" data-testid="eng-dashboard-loading">
        <div className="h-16 animate-pulse rounded-lg bg-zinc-200/70" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_x, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-zinc-200/70" />
          ))}
        </div>
        <div className="h-40 animate-pulse rounded-lg bg-zinc-200/70" />
      </div>
    );
  }

  return (
    <div className="space-y-7" data-testid="eng-dashboard">
      <PageHeader
        title="技術ダッシュボード"
        description="いま何を開発しているか、どこで止まっているかを一覧します。"
        testid="eng-dashboard-header"
        secondary={
          <form onSubmit={search} className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="開発案件を探す"
              className="w-56 pl-8" data-testid="eng-dashboard-search-input" />
          </form>
        }
        primary={
          <Button onClick={() => nav('/engineering/developments/new')} data-testid="eng-dashboard-new-dev-btn">
            <Plus size={15} className="mr-1" /> 新しい開発案件
          </Button>
        }
      />
      <DashboardZone title="対応が必要" hint="いま人の手が必要な案件です。ここから順に片付けます。" testid="dev-zone-attention">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <ActionTile icon={ShoppingCart} label="手配が必要" hint="部品依頼の作成待ち"
            value={c.need_procurement || 0} urgent={!!c.need_procurement}
            onClick={() => nav('/engineering/developments/readiness')} testid="dev-tile-need-procurement" />
          <ActionTile icon={Hammer} label="製作依頼" hint="自社製作の未完了（期限超過は赤）"
            value={fab?.open || 0} urgent={!!fab?.overdue}
            onClick={() => nav('/engineering/fabrication')} testid="dev-tile-fabrication" />
          <ActionTile icon={Clock} label={t('dev.tile.overdue')} hint={t('dev.tile.overdueHint')}
            value={c.overdue || 0} urgent={!!c.overdue}
            onClick={() => nav('/engineering/developments')} testid="dev-tile-overdue" />
          <ActionTile icon={Flask} label="検証待ち" hint="試作の結果を記録する"
            value={c.in_validation || 0}
            onClick={() => nav('/engineering/developments?stage=validation')} testid="dev-tile-validation" />
          <ActionTile icon={ArrowsClockwise} label="図面修正が必要" hint="不合格・修正待ち"
            value={c.needs_drawing_fix || 0} urgent={!!c.needs_drawing_fix}
            onClick={() => nav('/engineering/developments?stage=revision')} testid="dev-tile-revision" />
          <ActionTile icon={Blueprint} label="進行中" hint="動いている案件（下の一覧は要対応のみ）"
            value={c.in_progress || 0}
            onClick={() => nav('/engineering/developments')} testid="dev-tile-in-progress" />
        </div>
        {attention.length === 0
          ? <AllClear message="対応が必要な開発案件はありません" testid="dev-attention-allclear" />
          : (
            <ListCard title="確認が必要な案件" icon={WarningCircle} count={attention.length}
              testid="dev-attention-list">
              <div className="divide-y divide-zinc-200">
                {attention.map((d) => <DevRow key={d.id} d={d} onOpen={open} reason={d.reason} />)}
              </div>
            </ListCard>
          )}
      </DashboardZone>

      {(data?.procurement || []).length > 0 && (
        <DashboardZone title="部品と調達" hint="不足している部品と、部品依頼が必要な案件です。"
          testid="dev-zone-procurement">
          <ListCard title="調達リスクのある案件" icon={ShoppingCart}
            count={data.procurement.length} testid="dev-procurement-list">
            <div className="divide-y divide-zinc-200">
              {data.procurement.map((d) => <DevRow key={d.id} d={d} onOpen={open} />)}
            </div>
          </ListCard>
        </DashboardZone>
      )}

      {(data?.prototypes || []).length > 0 && (
        <DashboardZone title="試作と検証"
          hint={`いま試作・検証が進んでいる案件です。試作できる案件: ${c.build_ready || 0}件`}
          testid="dev-zone-prototypes"
          action={
            <Button variant="outline" size="sm" onClick={() => nav('/engineering/developments/readiness')}
              data-testid="dev-readiness-link-btn">試作の準備状況</Button>
          }>
          <ListCard title="試作中・検証中の案件" icon={Flask} count={data.prototypes.length}
            testid="dev-prototype-list">
            <div className="divide-y divide-zinc-200">
              {data.prototypes.map((d) => <DevRow key={d.id} d={d} onOpen={open} />)}
            </div>
          </ListCard>
        </DashboardZone>
      )}

      <DashboardZone title="最近の活動" testid="dev-zone-recent"
        action={
          <Button variant="outline" size="sm" onClick={() => nav('/engineering/developments')}
            data-testid="dev-see-all-btn">開発案件をすべて見る</Button>
        }>
        {(data?.recent || []).length === 0 ? (
          <EmptyState icon={Blueprint} title="開発案件はまだありません"
            description="構想提案を登録すると、ここに進行状況が表示されます。"
            testid="dev-recent-empty"
            action={<Button onClick={() => nav('/engineering/developments/new')}
              data-testid="dev-empty-new-btn">新しい開発案件</Button>} />
        ) : (
          <div className="rounded-lg border border-zinc-300 bg-white shadow-sm divide-y divide-zinc-200">
            {data.recent.map((d) => <DevRow key={d.id} d={d} onOpen={open} />)}
          </div>
        )}
      </DashboardZone>

      <DashboardZone title="データと傾向" hint="個別の案件を開かずに、失敗の傾向と費用を確認します。"
        testid="dev-zone-intel">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <ActionTile icon={ChartLine} label="全体傾向" hint="リードタイム・滞留・トラブル分析"
            value={c.in_progress || 0}
            onClick={() => nav('/engineering/developments/analytics')} testid="dev-tile-analytics" />
          <ActionTile icon={CurrencyJpy} label="原価分析" hint="種類別・部品別・月次"
            value={c.completed_this_month || 0}
            onClick={() => nav('/engineering/developments/cost')} testid="dev-tile-cost" />
          <ActionTile icon={GraduationCap} label="チームの学び" hint="繰り返す失敗と改善点"
            value={c.failed_validation || 0}
            onClick={() => nav('/engineering/developments/learning')} testid="dev-tile-learning" />
        </div>
      </DashboardZone>

      <MetricsDisclosure title="参考指標（ステージ別の件数）" testid="dev-metrics">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {(data?.by_stage || []).map((s) => (
            <div key={s.stage} className="rounded-md border border-zinc-300 bg-white px-3 py-2"
              data-testid={`dev-metric-${s.stage}`}>
              <p className="text-xs text-zinc-600">{s.label}</p>
              <p className="text-xl font-bold tabular-nums text-zinc-900">{s.count}</p>
            </div>
          ))}
        </div>
      </MetricsDisclosure>
    </div>
  );
}
